import React, { useState } from 'react';
import { useStore, uid } from '../state/store';
import {
  Card, Chip, Banner, Empty, Text, Area, Select, SectionHead, EvidenceBadge, Stat, Modal, Check,
} from '../components/ui';
import { ProductRequestForm } from '../components/Forms';
import { INFRA_STAGES, FEATURE_CLASSES, EVIDENCE_LEVELS, EMPTY_COPY } from '../lib/constants';
import { fmtMoney } from '../lib/derive';
import { parseCSV, rowsToRecords, OPP_ALIASES } from '../lib/io';

export default function Infrastructure() {
  const { state, d, set, add, patch, remove, today } = useStore();
  const infra = d.infra;
  const cur = state.settings?.currency;
  const [draft, setDraft] = useState(null);
  const [modal, setModal] = useState(null);

  const importCSV = (file) => {
    const reader = new FileReader();
    reader.onload = () => {
      const parsed = parseCSV(String(reader.result));
      const recs = rowsToRecords(parsed, OPP_ALIASES, { stage: 'Target', evidenceLevel: 'E0', tier: 'B', lastTouch: today });
      recs.forEach((r) => add('infra.opportunities', { id: uid('inf'), ...r }));
    };
    reader.readAsText(file);
  };

  return (
    <>
      <div className="page-head">
        <div className="row">
          <h2>BLOCKS INFRASTRUCTURE</h2>
          <Chip tone="green">Cash engine</Chip>
          <Chip>10% founder allocation</Chip>
        </div>
        <div className="sub">Commercialize the technology Blocks already owns as B2B infrastructure.</div>
      </div>

      <section className="grid g4">
        <Card><Stat label="Qualified prospects" value={<span className="num">{infra.qualified}</span>} /></Card>
        <Card><Stat label="Proposals" value={<span className="num">{infra.proposals}</span>} /></Card>
        <Card><Stat label="Paid POCs" value={<span className="num">{infra.paidPocs}</span>} /></Card>
        <Card><Stat label="Committed value" value={<span className="num">{fmtMoney(infra.committedValue, cur)}</span>} /></Card>
      </section>

      <section className="section">
        <SectionHead title="Closest path to cash" hint="The single most important field on this page." />
        {infra.closest ? (
          <Card>
            <div className="row" style={{ alignItems: 'flex-start' }}>
              <div style={{ flex: 1 }}>
                <div className="mid-num">{infra.closest.account || 'Unnamed account'}</div>
                <div className="muted" style={{ marginTop: 6 }}>
                  {infra.closest.stage} · {infra.closest.customerType || 'customer type not recorded'} ·{' '}
                  {infra.closest.country || 'country not recorded'}
                </div>
                <div style={{ marginTop: 10 }}>
                  <strong>Next step:</strong>{' '}
                  {infra.closest.nextAction || <span className="dim">undefined — that is the problem</span>}
                  {infra.closest.nextActionDue ? ` (due ${infra.closest.nextActionDue})` : ''}
                </div>
              </div>
              <div className="row tight">
                <EvidenceBadge level={infra.closest.evidenceLevel || 'E0'} showName />
                <button className="btn sm" onClick={() => setDraft(infra.closest)}>Open</button>
              </div>
            </div>
          </Card>
        ) : (
          <Empty title={EMPTY_COPY.infrastructure}>Add the first named buyer.</Empty>
        )}
      </section>

      <section className="section">
        <SectionHead
          title="Pipeline"
          hint={INFRA_STAGES.join(' → ')}
          right={
            <div className="row tight">
              <label className="btn sm">
                Import CSV
                <input
                  type="file"
                  accept=".csv,text/csv"
                  style={{ display: 'none' }}
                  onChange={(e) => e.target.files[0] && importCSV(e.target.files[0])}
                />
              </label>
              <button className="btn sm primary" onClick={() => setDraft(blankOpp(today))}>Add opportunity</button>
            </div>
          }
        />
        {infra.opps.length === 0 ? (
          <Empty title={EMPTY_COPY.infrastructure}>
            Target buyers: regulated operators, investment firms, asset managers, developers, contribution managers,
            fintechs, digital-asset firms.
          </Empty>
        ) : (
          <Card flush>
            <div className="table-wrap">
              <table className="table">
                <thead>
                  <tr>
                    <th>Account</th>
                    <th>Tier</th>
                    <th>Country</th>
                    <th>Stage</th>
                    <th>Evidence</th>
                    <th className="num">Proposal value</th>
                    <th>Next action</th>
                    <th className="num">Last touch</th>
                    <th className="actions" />
                  </tr>
                </thead>
                <tbody>
                  {infra.ranked.concat(infra.opps.filter((o) => o.stage === 'Lost / Parked')).map((o) => (
                    <tr key={o.id}>
                      <td>
                        <div>{o.account || <span className="dim">Unnamed</span>}</div>
                        <div className="dim tiny">{o.customerType}</div>
                      </td>
                      <td>{o.tier ? <Chip tone={o.tier === 'A' ? 'accent' : ''}>{o.tier}</Chip> : <span className="dim">—</span>}</td>
                      <td>{o.country}</td>
                      <td>{o.stage}</td>
                      <td><EvidenceBadge level={o.evidenceLevel || 'E0'} /></td>
                      <td className="num">{o.proposalValue ? fmtMoney(o.proposalValue, cur) : '—'}</td>
                      <td>{o.nextAction || <span className="dim">none</span>}</td>
                      <td className="num">{o.lastTouch || '—'}</td>
                      <td className="actions">
                        <button className="btn sm ghost" onClick={() => setDraft(o)}>Edit</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        )}
      </section>

      {/* -------------------------------------------------------- modules -- */}
      <section className="section">
        <SectionHead
          title="Product modules"
          hint="Classify every requested feature. CUSTOM requires a customer and an opportunity."
          right={<button className="btn sm" onClick={() => setModal('request')}>New product request</button>}
        />
        <Card flush>
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>Module</th>
                  <th style={{ width: 100 }}>Group</th>
                  <th style={{ width: 160 }}>Classification</th>
                  <th style={{ width: 160 }}>Opportunity ID</th>
                  <th>Note</th>
                </tr>
              </thead>
              <tbody>
                {infra.features.map((f) => (
                  <tr key={f.id} style={f.classification === 'CUSTOM' && !f.opportunityId ? { background: 'var(--red-bg)' } : undefined}>
                    <td>{f.name}</td>
                    <td><span className="dim tiny">{f.group}</span></td>
                    <td>
                      <select value={f.classification} onChange={(e) => patch('infra.features', f.id, { classification: e.target.value })}>
                        {FEATURE_CLASSES.map((c) => <option key={c} value={c}>{c}</option>)}
                      </select>
                    </td>
                    <td>
                      <input type="text" value={f.opportunityId || ''} placeholder="D001, INF-002" onChange={(e) => patch('infra.features', f.id, { opportunityId: e.target.value })} />
                    </td>
                    <td>
                      <input type="text" value={f.note || ''} onChange={(e) => patch('infra.features', f.id, { note: e.target.value })} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
        {infra.customWithoutDeal.length > 0 && (
          <div style={{ marginTop: 10 }}>
            <Banner tone="stop">
              <strong>{infra.customWithoutDeal.length} CUSTOM item(s) with no opportunity attached.</strong> Custom work
              requires a customer, an opportunity ID, an expected contract value, a reusable-value statement and founder
              approval.
            </Banner>
          </div>
        )}
      </section>

      {/* ------------------------------------------------ product requests - */}
      {(state.productRequests || []).length > 0 && (
        <section className="section">
          <SectionHead title="Product requests" />
          <Card flush>
            <div className="table-wrap">
              <table className="table">
                <thead>
                  <tr>
                    <th>Request</th>
                    <th>Customer</th>
                    <th>Opportunity</th>
                    <th>Class</th>
                    <th>Impact</th>
                    <th className="actions" />
                  </tr>
                </thead>
                <tbody>
                  {state.productRequests.map((p) => (
                    <tr key={p.id}>
                      <td>{p.request}</td>
                      <td>{p.customer || <span className="dim">—</span>}</td>
                      <td>{p.opportunityId || <span className="dim">none</span>}</td>
                      <td><Chip tone={p.classification === 'FROZEN' ? 'red' : 'green'}>{p.classification}</Chip></td>
                      <td>{p.commercialImpact || <span className="dim">—</span>}</td>
                      <td className="actions">
                        <button className="btn sm ghost" onClick={() => remove('productRequests', p.id)}>Remove</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        </section>
      )}

      {draft && (
        <OppModal
          draft={draft}
          cur={cur}
          onClose={() => setDraft(null)}
          onSave={(o) => {
            const exists = infra.opps.some((x) => x.id === o.id);
            if (exists) patch('infra.opportunities', o.id, o);
            else add('infra.opportunities', o);
          }}
          onDelete={(id) => remove('infra.opportunities', id)}
        />
      )}
      {modal === 'request' && <ProductRequestForm onClose={() => setModal(null)} />}
    </>
  );
}

function blankOpp(today) {
  return {
    id: uid('inf'),
    account: '',
    country: '',
    customerType: '',
    tier: 'B',
    stage: 'Target',
    existingPlatform: '',
    technologyGap: '',
    blocksModule: '',
    decisionMaker: '',
    budget: '',
    deploymentTiming: '',
    implementationRequirement: '',
    recurringRevenue: '',
    proposalValue: '',
    evidenceLevel: 'E0',
    nextAction: '',
    nextActionDue: '',
    lastTouch: today,
    paidPoc: false,
    isNew: true,
  };
}

function OppModal({ draft, onClose, onSave, onDelete, cur }) {
  const [o, setO] = useState(draft);
  const set = (k) => (v) => setO((x) => ({ ...x, [k]: v }));
  return (
    <Modal
      title={draft.isNew ? 'Add infrastructure opportunity' : draft.account || 'Opportunity'}
      onClose={onClose}
      wide
      footer={
        <>
          {!draft.isNew && <button className="btn danger" onClick={() => { onDelete(o.id); onClose(); }}>Delete</button>}
          <div className="spacer" />
          <button className="btn" onClick={onClose}>Cancel</button>
          <button className="btn primary" onClick={() => { onSave({ ...o, isNew: false }); onClose(); }}>Save</button>
        </>
      }
    >
      <div className="grid g3">
        <Text label="Account" value={o.account} onChange={set('account')} />
        <Text label="Country" value={o.country} onChange={set('country')} />
        <Select label="Tier" value={o.tier} onChange={set('tier')} options={['A', 'B', 'C']} />
        <Text label="Customer type" value={o.customerType} onChange={set('customerType')} placeholder="Regulated operator, asset manager…" />
        <Select label="Stage" value={o.stage} onChange={set('stage')} options={INFRA_STAGES} />
        <Select label="Evidence level" value={o.evidenceLevel} onChange={set('evidenceLevel')} options={EVIDENCE_LEVELS.map((e) => ({ value: e.id, label: `${e.id} — ${e.name}` }))} />
      </div>
      <div className="grid g2">
        <Text label="Existing platform" value={o.existingPlatform} onChange={set('existingPlatform')} />
        <Text label="Blocks module" value={o.blocksModule} onChange={set('blocksModule')} />
      </div>
      <Area label="Technology gap" value={o.technologyGap} onChange={set('technologyGap')} rows={2} placeholder="Their words, not your pitch." />
      <div className="grid g3">
        <Text label="Decision-maker" value={o.decisionMaker} onChange={set('decisionMaker')} />
        <Text label="Budget" value={o.budget} onChange={set('budget')} placeholder="Identified? Whose?" />
        <Text label="Deployment timing" value={o.deploymentTiming} onChange={set('deploymentTiming')} />
        <Text label="Implementation requirement" value={o.implementationRequirement} onChange={set('implementationRequirement')} />
        <Text label={`Recurring revenue${cur ? ` (${cur})` : ''}`} value={o.recurringRevenue} onChange={set('recurringRevenue')} />
        <Text label={`Proposal value${cur ? ` (${cur})` : ''}`} value={o.proposalValue} onChange={set('proposalValue')} />
      </div>
      <div className="grid g3">
        <Text label="Next action" value={o.nextAction} onChange={set('nextAction')} />
        <Text label="Next action due" type="date" value={o.nextActionDue} onChange={set('nextActionDue')} />
        <Text label="Last touch" type="date" value={o.lastTouch} onChange={set('lastTouch')} />
      </div>
      <Check label="Paid POC in place" checked={o.paidPoc} onChange={set('paidPoc')} />
    </Modal>
  );
}
